import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { 
    LightBulbIcon,
    TrophyIcon,
    RocketLaunchIcon,
    ExclamationTriangleIcon,
    CubeIcon,
    CurrencyDollarIcon,
    LinkIcon,
    ArrowPathIcon,
    ChartBarIcon,
    SparklesIcon,
    FireIcon,
    BoltIcon,
    CalendarIcon
} from '@heroicons/react/24/outline';
import api from '../utils/api';

function AIRecommendationsPanel() {
    const { token } = useAuth();
    const [recommendations, setRecommendations] = useState({
        recommendations: [],
        categoryAnalysis: [],
        loading: true
    });
    const [timeRange, setTimeRange] = useState('30');

    useEffect(() => {
        fetchRecommendations();
    }, [timeRange]);

    const fetchRecommendations = async () => {
        if (!token) return;

        try {
            setRecommendations(prev => ({ ...prev, loading: true }));
            
            // Önce debug endpoint'ini deneyelim
            console.log('AI önerileri yükleniyor...');
            
            const response = await api.get(`/api/gemini/category-insights?timeRange=${timeRange}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            // Response yapısını düzelt
            const responseData = response.data?.data || response.data || {};
            
            // Veriyi direkt kullan, deep copy yap
            const cleanRecommendations = responseData.recommendations ? JSON.parse(JSON.stringify(responseData.recommendations)) : [];
            const cleanCategoryAnalysis = responseData.categoryAnalysis ? JSON.parse(JSON.stringify(responseData.categoryAnalysis)) : [];
            
            // Gemini AI'dan gelen veriler + test verileri
            const testSeasonalOpportunities = [
                {
                    season: "Yaz Sezonu",
                    opportunity: "Yaz tatili öncesi çocuk giyim talebinde artış bekleniyor",
                    categories: ["Kız Elbise", "Erkek T-shirt"],
                    action: "Yaz koleksiyonu hazırlayın ve erken rezervasyon kampanyası başlatın",
                    timing: "Mayıs-Haziran"
                },
                {
                    season: "Okul Dönemi",
                    opportunity: "Eylül ayında okul kıyafetleri için yoğun talep",
                    categories: ["Erkek Gömlek", "Kız T-shirt"],
                    action: "Okul temalı ürünler ve toplu alım indirimleri sunun",
                    timing: "Ağustos-Eylül"
                }
            ];

            const testPricingStrategies = [
                {
                    category: "Kız Elbise",
                    current_avg_price: 139.9,
                    recommended_price_range: "₺120-180",
                    strategy: "Premium pozisyonlama ile fiyat artırımı",
                    reasoning: "Yüksek talep ve kalite algısı"
                },
                {
                    category: "Bebek Body",
                    current_avg_price: 89.9,
                    recommended_price_range: "₺70-110",
                    strategy: "Hacim odaklı fiyatlandırma",
                    reasoning: "Sık kullanım, çoklu alım teşviki"
                }
            ];

            const testCrossSellingOpportunities = [
                {
                    primary_category: "Kız Elbise",
                    complementary_categories: ["Kız T-shirt", "Bebek Body"],
                    bundle_suggestion: "Elbise + T-shirt kombinasyonu",
                    discount_strategy: "2. üründe %20 indirim"
                },
                {
                    primary_category: "Erkek Gömlek",
                    complementary_categories: ["Erkek T-shirt"],
                    bundle_suggestion: "Formal + casual kombin paketi",
                    discount_strategy: "Paket alımda %15 indirim"
                }
            ];

            const testQuickWins = [
                {
                    action: "Ürün fotoğraflarını iyileştirin",
                    impact: "Yüksek",
                    effort: "Düşük",
                    timeline: "1-2 hafta"
                },
                {
                    action: "Stokta az kalan ürünlerde acil satış kampanyası",
                    impact: "Orta",
                    effort: "Düşük",
                    timeline: "3-5 gün"
                },
                {
                    action: "Müşteri yorumlarını ürün sayfalarında öne çıkarın",
                    impact: "Orta",
                    effort: "Düşük",
                    timeline: "1 hafta"
                }
            ];

            setRecommendations({
                recommendations: cleanRecommendations,
                categoryAnalysis: cleanCategoryAnalysis,
                totalCategories: responseData.totalCategories || 0,
                generatedAt: responseData.generatedAt,
                // Gemini AI'dan gelen veriler (varsa) + test verileri
                seasonalOpportunities: responseData.seasonalOpportunities?.length > 0 ? responseData.seasonalOpportunities : testSeasonalOpportunities,
                pricingStrategies: responseData.pricingStrategies?.length > 0 ? responseData.pricingStrategies : testPricingStrategies,
                crossSellingOpportunities: responseData.crossSellingOpportunities?.length > 0 ? responseData.crossSellingOpportunities : testCrossSellingOpportunities,
                quickWins: responseData.quickWins?.length > 0 ? responseData.quickWins : testQuickWins,
                marketingCampaigns: responseData.marketingCampaigns || [],
                inventoryOptimization: responseData.inventoryOptimization || [],
                customerInsights: responseData.customerInsights || {},
                competitiveAnalysis: responseData.competitiveAnalysis || {},
                longTermStrategy: responseData.longTermStrategy || {},
                loading: false
            });
        } catch (error) {
            console.error('AI önerileri yüklenirken hata:', error);
            console.error('Error details:', error.response?.data);
            toast.error(`AI önerileri yüklenemedi: ${error.response?.data?.error || error.message}`);
            setRecommendations(prev => ({ ...prev, loading: false }));
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'high': return 'bg-red-100 text-red-800 border-red-200';
            case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'low': return 'bg-blue-100 text-blue-800 border-blue-200';
            default: return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const getImpactColor = (impact) => {
        switch (impact) {
            case 'Yüksek': return 'text-green-600 bg-green-100';
            case 'Orta': return 'text-yellow-600 bg-yellow-100';
            case 'Düşük': return 'text-blue-600 bg-blue-100';
            default: return 'text-gray-600 bg-gray-100';
        }
    };

    const getEffortColor = (effort) => {
        switch (effort) {
            case 'Düşük': return 'text-green-600 bg-green-100';
            case 'Orta': return 'text-yellow-600 bg-yellow-100';
            case 'Yüksek': return 'text-red-600 bg-red-100';
            default: return 'text-gray-600 bg-gray-100';
        }
    };

    const getTypeIcon = (type) => {
        switch (type) {
            case 'top_performer': return <TrophyIcon className="h-6 w-6" />;
            case 'fast_growing': return <RocketLaunchIcon className="h-6 w-6" />;
            case 'underperformer': return <ExclamationTriangleIcon className="h-6 w-6" />;
            case 'empty_category': return <CubeIcon className="h-6 w-6" />;
            case 'price_optimization': return <CurrencyDollarIcon className="h-6 w-6" />;
            case 'cross_selling': return <LinkIcon className="h-6 w-6" />;
            default: return <LightBulbIcon className="h-6 w-6" />;
        }
    };

    const RecommendationCard = ({ recommendation }) => (
        <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0 text-brand-green">
                        {getTypeIcon(recommendation.type)}
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                            {recommendation.title}
                            <span className="ml-2 text-2xl">{recommendation.icon}</span>
                        </h3>
                        <div className="flex items-center space-x-2 mt-1">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getPriorityColor(recommendation.priority)}`}>
                                {recommendation.priority === 'high' ? 'Yüksek Öncelik' : 
                                 recommendation.priority === 'medium' ? 'Orta Öncelik' : 'Düşük Öncelik'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mb-4">
                <p className="text-gray-700 mb-2">{recommendation.description}</p>
                <p className="text-brand-green font-medium">{recommendation.action}</p>
            </div>

            {recommendation.categories && recommendation.categories.length > 0 && (
                <div className="mb-4">
                    <p className="text-sm font-medium text-gray-500 mb-2">İlgili Kategoriler:</p>
                    <div className="flex flex-wrap gap-2">
                        {recommendation.categories.map((category, index) => (
                            <span key={index} className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                                {category}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-1">
                        <span className="text-xs text-gray-500">Etki:</span>
                        <span className={`px-2 py-1 text-xs font-medium rounded ${getImpactColor(recommendation.impact)}`}>
                            {recommendation.impact}
                        </span>
                    </div>
                    <div className="flex items-center space-x-1">
                        <span className="text-xs text-gray-500">Efor:</span>
                        <span className={`px-2 py-1 text-xs font-medium rounded ${getEffortColor(recommendation.effort)}`}>
                            {recommendation.effort}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );

    const CategoryAnalysisCard = ({ category }) => {
        // Veri kontrolü ve varsayılan değerler
        const productCount = category.productCount || 0;
        const totalSales = category.totalSales || 0;
        const revenue = category.revenue || 0;
        const trendScore = isNaN(category.trendScore) ? 0 : (category.trendScore || 0);
        const performanceScore = isNaN(category.performanceScore) ? 0 : (category.performanceScore || 0);
        
        return (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-900">{category.name || 'Bilinmeyen Kategori'}</h4>
                    <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            performanceScore >= 70 ? 'bg-green-100 text-green-800' :
                            performanceScore >= 40 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                        }`}>
                            {performanceScore.toFixed(0)} puan
                        </span>
                    </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                        <span className="text-gray-500">Ürün:</span>
                        <span className="ml-1 font-medium">{productCount}</span>
                    </div>
                    <div>
                        <span className="text-gray-500">Satış:</span>
                        <span className="ml-1 font-medium">{totalSales}</span>
                    </div>
                    <div>
                        <span className="text-gray-500">Gelir:</span>
                        <span className="ml-1 font-medium">₺{revenue.toFixed(2)}</span>
                    </div>
                    <div className="flex items-center">
                        <span className="text-gray-500">Trend:</span>
                        <span className={`ml-1 font-medium flex items-center ${
                            trendScore > 0 ? 'text-green-600' : 
                            trendScore < 0 ? 'text-red-600' : 'text-gray-600'
                        }`}>
                            {trendScore > 0 ? '↗️' : trendScore < 0 ? '↘️' : '➡️'}
                            {Math.abs(trendScore).toFixed(1)}%
                        </span>
                    </div>
                </div>
                
                {/* Debug bilgisi - geçici */}
                {process.env.NODE_ENV === 'development' && (
                    <div className="mt-2 text-xs text-gray-400 border-t pt-2">
                        Debug: {JSON.stringify({
                            productCount: category.productCount,
                            totalSales: category.totalSales, 
                            revenue: category.revenue,
                            trendScore: category.trendScore
                        })}
                    </div>
                )}
            </div>
        );
    };

    if (recommendations.loading) {
        return (
            <div className="space-y-6">
                <div className="animate-pulse">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                        {Array.from({ length: 4 }).map((_, i) => (
                            <div key={i} className="bg-gray-200 h-48 rounded-lg"></div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                        <SparklesIcon className="h-8 w-8 text-brand-green mr-3" />
                        AI Kategori Önerileri
                    </h2>
                    <p className="text-gray-600 mt-2">Yapay zeka destekli satış artırma önerileri</p>
                    {recommendations.generatedAt && (
                        <p className="text-sm text-gray-500 mt-1">
                            Son güncelleme: {new Date(recommendations.generatedAt).toLocaleString('tr-TR')}
                        </p>
                    )}
                </div>
                <div className="flex items-center space-x-4">
                    <select
                        value={timeRange}
                        onChange={(e) => setTimeRange(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-green"
                    >
                        <option value="7">Son 7 gün</option>
                        <option value="30">Son 30 gün</option>
                        <option value="90">Son 90 gün</option>
                    </select>
                    <button
                        onClick={fetchRecommendations}
                        className="inline-flex items-center px-4 py-2 bg-brand-green text-white rounded-lg hover:bg-brand-green-dark transition-colors"
                    >
                        <ArrowPathIcon className="h-4 w-4 mr-2" />
                        Yenile
                    </button>
                </div>
            </div>

            {/* Özet Kartları */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 rounded-lg text-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-blue-100">Toplam Öneri</p>
                            <p className="text-3xl font-bold">{recommendations.recommendations?.length || 0}</p>
                        </div>
                        <LightBulbIcon className="h-12 w-12 text-blue-200" />
                    </div>
                </div>

                <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 rounded-lg text-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-green-100">Yüksek Öncelik</p>
                            <p className="text-3xl font-bold">
                                {recommendations.recommendations?.filter(r => r.priority === 'high').length || 0}
                            </p>
                        </div>
                        <FireIcon className="h-12 w-12 text-green-200" />
                    </div>
                </div>

                <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-6 rounded-lg text-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-purple-100">Analiz Edilen Kategori</p>
                            <p className="text-3xl font-bold">{recommendations.totalCategories || 0}</p>
                        </div>
                        <ChartBarIcon className="h-12 w-12 text-purple-200" />
                    </div>
                </div>
            </div>

            {/* Stratejik AI Önerileri */}
            <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                    <BoltIcon className="h-6 w-6 text-yellow-500 mr-2" />
                    Stratejik AI Önerileri
                </h3>
                {recommendations.recommendations?.length > 0 ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {recommendations.recommendations.map((recommendation, index) => (
                            <RecommendationCard key={index} recommendation={recommendation} />
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                        <SparklesIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">AI analizi yükleniyor</h3>
                        <p className="text-gray-500">Gemini AI verilerinizi analiz ediyor...</p>
                    </div>
                )}
            </div>

            {/* Sezonsal Fırsatlar */}
            {recommendations.seasonalOpportunities?.length > 0 && (
                <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                        <CalendarIcon className="h-6 w-6 text-green-500 mr-2" />
                        Sezonsal Fırsatlar
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {recommendations.seasonalOpportunities.map((opportunity, index) => (
                            <div key={index} className="bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-lg border border-green-200">
                                <div className="flex items-center justify-between mb-3">
                                    <h4 className="font-semibold text-gray-900">{opportunity.season}</h4>
                                    <span className="text-2xl">🌟</span>
                                </div>
                                <p className="text-gray-700 mb-3">{opportunity.opportunity}</p>
                                <p className="text-green-700 font-medium mb-2">{opportunity.action}</p>
                                <div className="flex items-center justify-between">
                                    <div className="flex flex-wrap gap-1">
                                        {opportunity.categories?.map((cat, i) => (
                                            <span key={i} className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">
                                                {cat}
                                            </span>
                                        ))}
                                    </div>
                                    <span className="text-sm text-gray-600">{opportunity.timing}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Fiyat Stratejileri */}
            {recommendations.pricingStrategies?.length > 0 && (
                <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                        <CurrencyDollarIcon className="h-6 w-6 text-blue-500 mr-2" />
                        Fiyat Stratejileri
                    </h3>
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kategori</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mevcut Ort. Fiyat</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Önerilen Aralık</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Strateji</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {recommendations.pricingStrategies.map((strategy, index) => (
                                        <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                            <td className="px-6 py-4 text-sm font-medium text-gray-900">{strategy.category}</td>
                                            <td className="px-6 py-4 text-sm text-gray-700">₺{strategy.current_avg_price}</td>
                                            <td className="px-6 py-4 text-sm text-gray-700">{strategy.recommended_price_range}</td>
                                            <td className="px-6 py-4 text-sm text-gray-700">{strategy.strategy}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Cross-Selling Fırsatları */}
            {recommendations.crossSellingOpportunities?.length > 0 && (
                <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                        <LinkIcon className="h-6 w-6 text-purple-500 mr-2" />
                        Cross-Selling Fırsatları
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {recommendations.crossSellingOpportunities.map((opportunity, index) => (
                            <div key={index} className="bg-purple-50 p-6 rounded-lg border border-purple-200">
                                <h4 className="font-semibold text-gray-900 mb-2">{opportunity.primary_category}</h4>
                                <p className="text-gray-700 mb-3">{opportunity.bundle_suggestion}</p>
                                <div className="mb-3">
                                    <span className="text-sm text-gray-600">Tamamlayıcı kategoriler:</span>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                        {opportunity.complementary_categories?.map((cat, i) => (
                                            <span key={i} className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded">
                                                {cat}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                <p className="text-purple-700 font-medium text-sm">{opportunity.discount_strategy}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Hızlı Kazançlar */}
            {recommendations.quickWins?.length > 0 && (
                <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                        <BoltIcon className="h-6 w-6 text-orange-500 mr-2" />
                        Hızlı Kazançlar
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {recommendations.quickWins.map((win, index) => (
                            <div key={index} className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                                <h4 className="font-semibold text-gray-900 mb-2">{win.action}</h4>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Etki:</span>
                                        <span className="font-medium">{win.impact}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Efor:</span>
                                        <span className="font-medium">{win.effort}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Süre:</span>
                                        <span className="font-medium">{win.timeline}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Kategori Performans Analizi */}
            <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                    <ChartBarIcon className="h-6 w-6 text-brand-green mr-2" />
                    Kategori Performans Analizi
                </h3>
                {recommendations.categoryAnalysis?.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {recommendations.categoryAnalysis.map((category, index) => (
                            <CategoryAnalysisCard key={index} category={category} />
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-8 bg-white rounded-lg border border-gray-200">
                        <p className="text-gray-500">Kategori analizi için yeterli veri bulunmuyor</p>
                    </div>
                )}
            </div>
        </div>
    );
}

export default AIRecommendationsPanel;